/**
 * What the top bar says (z8nrz7e46x): "Header - Title".
 *
 * The header is the section the route belongs to (`/procedures` is Transplants);
 * the title is what the open page calls itself (the same route lists Kits, MES
 * or CAPA depending on its type). When they match, the header alone — never
 * "Transplants - Transplants".
 */
export function topBarTitle(header: string | null, pageTitle: string | null | undefined): string {
  const title = pageTitle?.trim()
  // No section for the route: the page's own name, or nothing at all.
  if (!header) return title ?? ''
  if (!title || title.toLowerCase() === header.trim().toLowerCase()) return header
  return `${header} - ${title}`
}

/**
 * The section each route belongs to, first match wins. An entry ending in `/`
 * matches everything under it; any other entry matches only itself.
 *
 * There is no catch-all. A bare `'/'` prefix entry matched every path, so any
 * page missing above it read "Home" in the top bar.
 */
const SECTION_TITLES: [string, string][] = [
  ['/patients/', 'Patients'],
  ['/patients', 'Patients'],
  ['/forms/', 'Patient Forms'],
  ['/forms', 'Collection'],
  ['/submissions', 'All Submissions'],
  ['/form-definitions', 'Form Definitions'],
  ['/form-builder/', 'Form Builder'],
  ['/form-builder', 'Form Builder'],
  ['/audit', 'Audit Trail Center'],
  ['/wiki/', 'Wiki'],
  ['/wiki', 'Wiki'],
  ['/procedures/calendar', 'Upcoming Transplants'],
  ['/board', 'Overall Status'],
  ['/procedures', 'Transplants'],
  // Their own sections, not Transplants: an MES order or a kit run is never a
  // transplant (mes.smoke.spec), and without these both fell through to
  // "CGT Application".
  ['/mes/', 'MES'],
  ['/mes', 'MES'],
  ['/kits/', 'Kits'],
  ['/kits', 'Kits'],
  // The Equipment page for rooms (z8nrz7ewt3), and a task's procedure run
  // opened from either page's Pending Actions (z8nrz7ewt1).
  ['/cleaning/', 'Cleaning'],
  ['/cleaning', 'Cleaning'],
  ['/equipment/', 'Equipment'],
  ['/equipment', 'Equipment'],
  ['/procedure-templates/', 'Procedure Template'],
  ['/procedure-templates', 'Procedure Templates'],
  // Pick lists, storage transfers, a storage unit, a kit build: all Inventory.
  ['/inventory/', 'Inventory'],
  ['/inventory', 'Inventory'],
  ['/products/', 'Products'],
  ['/products', 'Products'],
  ['/configure', 'Configure'],
  ['/profile', 'Profile'],
  ['/my-steps', 'My Work'],
  ['/capa', 'CAPA'],
  ['/dashboards', 'Dashboards'],
  ['/shift-dashboard', 'Shift Dashboard'],
  ['/reports/', 'Reports'],
  ['/reports', 'Reports'],
  ['/notifications', 'Notifications'],
  ['/settings', 'Settings'],
  ['/tenancy-setup', 'Tenancy Setup'],
  ['/integrations/', 'Integrations'],
  ['/integrations', 'Integrations'],
  ['/components', 'Components'],
  // Resource Center (z8nrz7dhzg) — fell through to "Home" before.
  ['/resources/', 'Resources'],
  ['/resources', 'Resources'],
]

/** The section a route belongs to, or null when the table has none. */
export function sectionTitle(pathname: string): string | null {
  for (const [path, title] of SECTION_TITLES) {
    if (path.endsWith('/') ? pathname.startsWith(path) : pathname === path) return title
  }
  return null
}

